/**
 * @file address.ts
 * @module app/modules/_common/utils/address
 * @description Utility functions for formatting delivery addresses.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 *
 * Formats delivery details into a multi-line string.
 * Line order:
 * 1) Name
 * 2) Address line 1
 * 3) Landmark, ward, district, city
 * 4) Phone country + phone
 */
export type DeliveryAddressLike = {
  name?: string;
  phoneCountry?: string;
  phone?: string;
  addressLine1?: string;
  landmark?: string;
  wardName?: string;
  districtName?: string;
  city?: string;
};

/**
 * Options for formatting a delivery address.
 */
export type FormatDeliveryAddressOptions = {
  showOnlyAddress?: boolean;
  maxChars?: number;
};

/**
 * Applies a maximum character limit to a string, adding ellipsis if truncated.
 * @param value The string to apply the character limit to.
 * @param maxChars The maximum number of characters allowed.
 * @returns The truncated string with ellipsis if it exceeded the limit.
 */
const applyMaxChars = (value: string, maxChars?: number) => {
  if (!maxChars || maxChars < 1) return value;
  if (value.length <= maxChars) return value;

  const safeLimit = Math.max(1, maxChars - 3);
  return `${value.slice(0, safeLimit).trimEnd()}...`;
};

/**
 * Formats a delivery address into a multi-line string.
 */
export const formatDeliveryAddress = (
  delivery?: DeliveryAddressLike,
  options: FormatDeliveryAddressOptions = { showOnlyAddress: false }
) => {
  const { showOnlyAddress = false, maxChars } = options;
  const name = (delivery?.name || "").trim();
  const addressLine1 = (delivery?.addressLine1 || "").trim();
  const phone = [delivery?.phoneCountry, delivery?.phone]
    .filter((value) => value && String(value).trim().length > 0)
    .join(" ");

  const localityLine = [
    delivery?.landmark,
    delivery?.wardName,
    delivery?.districtName,
    delivery?.city,
  ].filter((value) => value && String(value).trim().length > 0);

  if (showOnlyAddress) {
    const formattedAddressOnly = [addressLine1, localityLine.join(", ")]
      .filter((value) => value && String(value).trim().length > 0)
      .join("\n");

    return applyMaxChars(formattedAddressOnly, maxChars);
  }

  const formattedFullAddress = [name, addressLine1, localityLine.join(", "), phone]
    .filter((value) => value && String(value).trim().length > 0)
    .join("\n");

  return applyMaxChars(formattedFullAddress, maxChars);
};